'use client'
import React, { useEffect, useMemo, useState } from 'react';
import { Control, UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayMove, FieldArrayWithId, FieldErrors, useWatch, UseFormSetValue } from 'react-hook-form';
import { GripVertical } from 'lucide-react';
import InputField from '../InputField';
import Button from '../Button';
import { ExtendedFormData } from './types';

interface SelectOptionsProps {
  selectedType: string;
  fields: FieldArrayWithId<ExtendedFormData, "options", "id">[];
  control: Control<ExtendedFormData>;
  errors: FieldErrors<ExtendedFormData>;
  append: UseFieldArrayAppend<ExtendedFormData, "options"> | ((data: unknown) => void);
  remove: UseFieldArrayRemove;
  move?: UseFieldArrayMove;
  disabled?: boolean;
  setValue?: UseFormSetValue<ExtendedFormData>;
  showQualifiedCheckbox?: boolean; // Add this prop to control visibility of qualified checkbox
  showIsWonCheckbox?: boolean; // Add this prop to control visibility of is_won checkbox
}

const SelectOptions: React.FC<SelectOptionsProps> = ({
  selectedType,
  fields,
  control,
  errors,
  append,
  remove,
  move,
  disabled = false,
  setValue,
  showQualifiedCheckbox = false, // Default to false
  showIsWonCheckbox = false, // Default to false
}) => {
  // Drag and drop state
  const [dragItem, setDragItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);

  // Watch the entire options array
  const options = useWatch({
    control,
    name: 'options',
  });

  // Memoized options to prevent unnecessary re-renders
  const memoizedOptions = useMemo(() => options || [], [options]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    if (disabled) return;
    setDragItem(index);
    e.dataTransfer?.setData('text/plain', ''); // Required for Firefox
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault();
    if (disabled || dragItem === null) return;
    setDragOverItem(index);
  };

  const handleDragDrop = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    if (disabled || dragItem === null || dragOverItem === null || !move || dragItem === dragOverItem) return;

    // Perform the move
    move(dragItem, dragOverItem);

    // Update opt_order for the moved options
    if (setValue && memoizedOptions) {
      const updatedOptions = [...memoizedOptions];

      // Reorder the opt_order values
      updatedOptions.forEach((option, index) => {
        option.opt_order = index + 1;
      });

      setValue('options', updatedOptions, { shouldValidate: true });
    }

    // Reset drag states
    setDragItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverItem(null);
  };

  // Auto-generate values from labels and assign opt_order
  useEffect(() => {
    if (!memoizedOptions || !Array.isArray(memoizedOptions) || memoizedOptions.length === 0 || !setValue) {
      return;
    }
    if (memoizedOptions && memoizedOptions.length > 0) {
      let hasUpdates = false;
      const updatedOptions = [...memoizedOptions];

      memoizedOptions.forEach((option, index) => {
        let needsUpdate = false;
        const updatedOption = { ...option };

        // Generate value from label if needed
        if (option.label && !option.value) {
          const generatedValue = option.label
            .toLowerCase()
            .replace(/[^\w\s]/gi, '')
            .replace(/\s+/g, '_');

          updatedOption.value = generatedValue;
          needsUpdate = true;
        }

        // Ensure opt_order exists
        if (option.opt_order === undefined) {
          updatedOption.opt_order = index + 1;
          needsUpdate = true;
        }

        // Ensure active is a boolean
        if (typeof option.active !== 'boolean') {
          updatedOption.active = option.active === 'true' || option.active === true;
          needsUpdate = true;
        }

        // Ensure qualified is a boolean
        if (option.qualified === undefined) {
          updatedOption.qualified = false;
          needsUpdate = true;
        } else if (typeof option.qualified !== 'boolean') {
          updatedOption.qualified = option.qualified === 'true';
          needsUpdate = true;
        }

        // Ensure is_won is a boolean
        if (option.is_won === undefined) {
          updatedOption.is_won = false;
          needsUpdate = true;
        } else if (typeof option.is_won !== 'boolean') {
          updatedOption.is_won = option.is_won === 'true';
          needsUpdate = true;
        }

        if (needsUpdate) {
          updatedOptions[index] = updatedOption;
          hasUpdates = true;
        }
      });

      // Only update if we actually made changes and setValue is available
      if (hasUpdates && setValue) {
        setValue('options', updatedOptions, { shouldValidate: true });
      }
    }
  }, [memoizedOptions, setValue]);

  if (selectedType !== 'select') return null;

  // Handle changing active state with explicit boolean conversion
  const handleActiveChange = (index: number, isActive: boolean) => {
    if (!setValue || !memoizedOptions || !Array.isArray(memoizedOptions)) return;

    const updatedOptions = [...memoizedOptions];
    if (index >= 0 && index < updatedOptions.length) {
      updatedOptions[index] = {
        ...updatedOptions[index],
        active: isActive
      };
      setValue('options', updatedOptions, { shouldValidate: true });
    }
  };

  // Handle changing qualified state
  const handleQualifiedChange = (index: number, isChecked: boolean) => {
    if (!setValue || !memoizedOptions || !Array.isArray(memoizedOptions)) return;

    const updatedOptions = [...memoizedOptions];
    if (index >= 0 && index < updatedOptions.length) {
      updatedOptions[index] = {
        ...updatedOptions[index],
        qualified: isChecked
      };
      setValue('options', updatedOptions, { shouldValidate: true });
    }
  };

  // Handle changing is_won state - only one can be checked at a time
  const handleIsWonChange = (index: number, isChecked: boolean) => {
    if (!setValue || !memoizedOptions || !Array.isArray(memoizedOptions)) return;

    const updatedOptions = [...memoizedOptions];

    if (isChecked) {
      // If checking this one, uncheck all others
      updatedOptions.forEach((option, idx) => {
        updatedOptions[idx] = {
          ...option,
          is_won: idx === index
        };
      });
    } else {
      // Just uncheck this one
      if (index >= 0 && index < updatedOptions.length) {
        updatedOptions[index] = {
          ...updatedOptions[index],
          is_won: false
        };
      }
    }

    setValue('options', updatedOptions, { shouldValidate: true });
  };

  // Handle adding a new option
  const handleAddOption = () => {
    if (typeof append === 'function') {
      const nextOrder = memoizedOptions?.length ? memoizedOptions.length + 1 : 1;
      append({
        label: '',
        value: '',
        active: true,
        qualified: false,
        is_won: false,
        opt_order: nextOrder
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-md overflow-hidden background-elements">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 ">
              <th className="p-2 text-left w-16">Order</th>
              <th className="p-2 text-left">Label</th>
              <th className="p-2 text-left w-32">Active</th>
              {showQualifiedCheckbox && <th className="py-2 pr-2 text-center w-16">Qualified</th>}
              {showIsWonCheckbox && <th className="py-2 pr-2 text-center w-16">Won</th>}
              {!disabled && <th className="p-2 text-center w-16">Action</th>}
            </tr>
          </thead>
          <tbody>
            {fields && fields.length > 0 ? fields.map((field, index) => (
              <tr
                key={field.id}
                className={`
                  ${index % 2 === 0 ? 'background-elements' : 'bg-gray-50 dark:bg-gray-700'}
                  ${dragItem === index ? 'opacity-50' : ''}
                  ${dragOverItem === index ? 'border-2 border-blue-500' : ''}
                `}
                draggable={!disabled && !!move}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={handleDragDrop}
                onDragEnd={handleDragEnd}
              >
                <td className="p-2 text-center">
                  <div className="flex items-center justify-center space-x-1">
                    {!disabled && move && (
                      <div className="cursor-move text-gray-400">
                        <GripVertical className="h-4 w-4" />
                      </div>
                    )}
                    <span className="text-sm">{index + 1}</span>
                    <InputField
                      id={`options.${index}.opt_order`}
                      name={`options.${index}.opt_order`}
                      type="hidden"
                      control={control}
                      hideLabel
                    />
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className={`${!memoizedOptions?.[index]?.active ? 'opacity-50' : ''}`}>
                    <InputField
                      id={`options.${index}.label`}
                      name={`options.${index}.label`}
                      type="text"
                      control={control}
                      error={errors?.options?.[index]?.label?.message}
                      hideLabel
                      disabled={disabled}
                    />
                    {!memoizedOptions?.[index]?.active && (
                      <div className="text-xs text-gray-500 mt-1">Inactive</div>
                    )}
                  </div>
                  <InputField
                    id={`options.${index}.value`}
                    name={`options.${index}.value`}
                    type="hidden"
                    control={control}
                    error={errors?.options?.[index]?.value?.message}
                    hideLabel
                    disabled={disabled}
                  />
                  {field.uuid && (
                    <InputField
                      id={`options.${index}.uuid`}
                      name={`options.${index}.uuid`}
                      type="hidden"
                      control={control}
                      hideLabel
                    />
                  )}
                </td>
                <td className="text-center">
                  <div className="flex items-center justify-center">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-brandColor"
                        checked={Boolean(memoizedOptions?.[index]?.active)}
                        onChange={(e) => handleActiveChange(index, e.target.checked)}
                        disabled={disabled}
                      />
                    </label>
                    <InputField
                      id={`options.${index}.active`}
                      name={`options.${index}.active`}
                      type="hidden"
                      control={control}
                      hideLabel
                    />
                  </div>
                </td>
                {showQualifiedCheckbox && (
                  <td className="text-center">
                    <div className="flex items-center justify-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-brandColor"
                          checked={Boolean(memoizedOptions?.[index]?.qualified)}
                          onChange={(e) => handleQualifiedChange(index, e.target.checked)}
                          disabled={disabled}
                        />
                        {/* <span className="ml-2">Qualified</span> */}
                      </label>
                      <InputField
                        id={`options.${index}.qualified`}
                        name={`options.${index}.qualified`}
                        type="hidden"
                        control={control}
                        hideLabel
                      />
                    </div>
                  </td>
                )}
                {/* Always include a hidden field for qualified in a proper table cell */}
                {!showQualifiedCheckbox && (
                  <td className="hidden text-center">
                    <InputField
                      id={`options.${index}.qualified`}
                      name={`options.${index}.qualified`}
                      type="hidden"
                      control={control}
                      hideLabel
                    />
                  </td>
                )}
                {showIsWonCheckbox && (
                  <td className="text-center">
                    <div className="flex items-center justify-center">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-green-600"
                          checked={Boolean(memoizedOptions?.[index]?.is_won)}
                          onChange={(e) => handleIsWonChange(index, e.target.checked)}
                          disabled={disabled}
                        />
                      </label>
                      <InputField
                        id={`options.${index}.is_won`}
                        name={`options.${index}.is_won`}
                        type="hidden"
                        control={control}
                        hideLabel
                      />
                    </div>
                  </td>
                )}
                {/* Always include a hidden field for is_won */}
                {!showIsWonCheckbox && (
                  <td className="hidden text-center">
                    <InputField
                      id={`options.${index}.is_won`}
                      name={`options.${index}.is_won`}
                      type="hidden"
                      control={control}
                      hideLabel
                    />
                  </td>
                )}
                {!disabled && (
                  <td className="py-2 px-3 text-center">
                    {/* Only show delete button for new options (no uuid) or if there's more than 1 option and it's not saved yet */}
                    {fields.length > 1 && !field.uuid && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-500 hover:text-red-700 text-xl font-bold"
                        aria-label="Remove option"
                        title="Delete this option"
                      >
                        ×
                      </button>
                    )}
                    {/* Show info icon for existing options */}
                    {field.uuid && (
                      <span className="text-gray-400 text-xs" title="Use 'Active' checkbox to deactivate existing options">
                        ⓘ
                      </span>
                    )}
                  </td>
                )}
              </tr>
            )) : (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-center text-gray-500">
                  No options available. Click "Add New Option" below to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add New Option button moved to after the table */}
      {!disabled && (
        <div className="flex justify-center">
          <Button
            btnStyle="brandBtn"
            className="px-4 py-2 text-sm"
            onClick={handleAddOption}
            type="button"
          >
            Add New Option
          </Button>
        </div>
      )}
    </div>
  );
};

export default SelectOptions;